import { c as _c } from "react/compiler-runtime";
import { createContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { AuthLoading } from '@app/(legacy)/auth/_components/AuthLoading';
import { Unauthorized } from '@app/(legacy)/auth/_components/Unauthorized';
import { SoftphoneSession } from '@components/contacts/sessions/SoftphoneSession';
import { useQueryGetMyUser } from '@hooks/query/users/useQueryGetMyUser';
import { useIsPopupWindow } from '@hooks/useIsPopupWindow';
import { signInAction } from '@lib/actions';
import { SoftphoneConnectionBehaviorEnum, TimeEnum } from '@lib/enums';
import { appAtom } from '@states/app/appAtom';
import { useSetJotaiAtom } from '@states/jotai/hooks';
import { getSoftphoneConnectionBehavior } from '@states/softphone/getSoftphoneConnectionBehavior';
import { softphoneAtom } from '@states/softphone/softphoneAtom';
import type { TDashboardUx } from '@lib/types/generated/TPermission';
import type { TUser } from '@models/authorization/TUser';
import type { FC, ReactNode } from 'react';

/**
 * Value exposed by UserContext.
 * Guaranteed to contain user once provider renders children.
 **/
export type TUserState = Readonly<{
  user: TUser;
}>;

/**
 * Provides authenticated user.
 */
export const UserContext = createContext<TUserState | null>(null);
type Props = {
  children: ReactNode;
};
type AuthenticatedUserProviderProps = Props & {
  callbackUrl?: string;
};

// Only mounts after auth, so SSR never builds the user query.
const AuthenticatedUserProvider: FC<AuthenticatedUserProviderProps> = t0 => {
  const $ = _c(25);
  const {
    children,
    callbackUrl
  } = t0;
  const isPopupWindow = useIsPopupWindow();
  const [isAppReady, setIsAppReady] = useState(false);
  const setAppAtom = useSetJotaiAtom(appAtom);
  const setSoftphoneAtom = useSetJotaiAtom(softphoneAtom);
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError
  } = useQueryGetMyUser();
  let t1;
  let t2;
  if ($[0] !== isPopupWindow || $[1] !== setAppAtom || $[2] !== setSoftphoneAtom || $[3] !== user) {
    t1 = () => {
      if (user == null || isPopupWindow) {
        setSoftphoneAtom(_temp);
        setIsAppReady(true);
        return;
      }
      const items = [...new Set(user.v2?.roles.map(_temp2) ?? [])] as TDashboardUx[];
      const connectionBehavior = getSoftphoneConnectionBehavior(user.roles);
      setSoftphoneAtom(curr => ({
        ...curr,
        connectionBehavior
      }));
      setAppAtom(curr_0 => ({
        ...curr_0,
        dashboard: items
      }));
      setIsAppReady(true);
    };
    t2 = [setSoftphoneAtom, setAppAtom, isPopupWindow, user];
    $[0] = isPopupWindow;
    $[1] = setAppAtom;
    $[2] = setSoftphoneAtom;
    $[3] = user;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== user) {
    t3 = () => {
      if (user != null) {
        const sentryUser = {
          id: user.azureAdId,
          email: user.emailAddress.toLowerCase()
        };
        Sentry.setUser(sentryUser);
      }
      return _temp3;
    };
    t4 = [user];
    $[6] = user;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== userError) {
    t5 = () => {
      if (userError) {
        Sentry.captureException(userError);
      }
    };
    t6 = [userError];
    $[9] = userError;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (isUserLoading) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AuthLoading reason="Fetching account" />;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  if (isUserError) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <>Something went wrong during sign in.<br />Please try again or contact IT if the problem continues.</>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== callbackUrl) {
      t8 = <Unauthorized action={signInAction} buttonLabel="Try again" callbackUrl={callbackUrl} message={t7} />;
      $[14] = callbackUrl;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  if (!isAppReady) {
    let t7;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AuthLoading reason="Setting up KITT" />;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  const t7 = user as TUser;
  let t8;
  if ($[17] !== t7) {
    t8 = {
      user: t7
    };
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== children || $[20] !== isPopupWindow) {
    t9 = isPopupWindow ? children : <SoftphoneSession>{children}</SoftphoneSession>;
    $[19] = children;
    $[20] = isPopupWindow;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <UserContext.Provider value={t8}>{t9}</UserContext.Provider>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};

/**
 * Provider for post-auth setup.
 *
 * Lifecycle:
 * 1. Loading.
 * 2. Auth check, redirects unauthenticated users.
 * 3. User fetch, calls API to hydrate full `TUser`.
 * 4. Role setup, derive dashboard visibility + softphone behavior from roles.
 * 5. Ready.
 */
export const UserContextProvider: FC<Props> = t0 => {
  const $ = _c(25);
  const {
    children
  } = t0;
  const {
    data: session,
    status,
    update
  } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const [refreshRetried, setRefreshRetried] = useState(false);
  let t1;
  if ($[0] !== refreshRetried || $[1] !== session?.error || $[2] !== update) {
    t1 = () => {
      if (session?.error === "RefreshTokenError" && !refreshRetried) {
        setRefreshRetried(true);
        update().catch(_temp4);
      }
      if (session?.error == null) {
        setRefreshRetried(false);
      }
    };
    $[0] = refreshRetried;
    $[1] = session?.error;
    $[2] = update;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = session?.error;
  let t3;
  if ($[4] !== refreshRetried || $[5] !== t2 || $[6] !== update) {
    t3 = [t2, refreshRetried, update];
    $[4] = refreshRetried;
    $[5] = t2;
    $[6] = update;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t1, t3);
  const isAuthenticated = status === "authenticated";
  let t4;
  let t5;
  if ($[8] !== pathname || $[9] !== router || $[10] !== status) {
    t4 = () => {
      if (status === "unauthenticated" && !pathname?.startsWith("/auth")) {
        const params = new URLSearchParams();
        if (pathname && pathname !== "/") {
          const search = window.location.search;
          params.set("callbackUrl", search ? `${pathname}${search}` : pathname);
        }
        const query = params.toString();
        const target = query ? `/auth?${query}` : "/auth";
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Redirecting unauthenticated user to /auth",
          level: "warning",
          data: {
            from: pathname,
            to: target
          }
        });
        router.replace(target);
      }
    };
    t5 = [status, pathname, router];
    $[8] = pathname;
    $[9] = router;
    $[10] = status;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  if (status === "loading") {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AuthLoading reason="Authenticating..." />;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t6;
  bb0: {
    if (!pathname) {
      t6 = undefined;
      break bb0;
    }
    const search_0 = typeof window !== "undefined" ? window.location.search : "";
    t6 = search_0 ? `${pathname}${search_0}` : pathname;
  }
  const fullCallbackUrl = t6;
  if (session?.error === "RefreshTokenError") {
    if (!refreshRetried) {
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <AuthLoading reason="Authenticating..." />;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      return t7;
    }
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Session has RefreshTokenError \u2014 showing sign-in prompt",
      level: "warning",
      data: {
        pathname,
        hasAccessToken: !!session.accessToken
      }
    });
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <>Your session has expired.<br />Please sign in again to continue.</>;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== fullCallbackUrl) {
      t8 = <Unauthorized action={signInAction} buttonLabel="Sign in" callbackUrl={fullCallbackUrl} message={t7} />;
      $[16] = fullCallbackUrl;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  if (!isAuthenticated) {
    if (pathname?.startsWith("/auth")) {
      let t7;
      if ($[18] !== children) {
        t7 = <>{children}</>;
        $[18] = children;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      return t7;
    }
    let t7;
    if ($[20] !== fullCallbackUrl) {
      t7 = <AuthLoading reason="Redirecting to login" fallbackAction={signInAction} fallbackCallbackUrl={fullCallbackUrl} fallbackDelayMs={TimeEnum.FiveSeconds} />;
      $[20] = fullCallbackUrl;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t7;
  if ($[22] !== children || $[23] !== fullCallbackUrl) {
    t7 = <AuthenticatedUserProvider callbackUrl={fullCallbackUrl}>{children}</AuthenticatedUserProvider>;
    $[22] = children;
    $[23] = fullCallbackUrl;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};
function _temp(current) {
  return {
    ...current,
    connectionBehavior: SoftphoneConnectionBehaviorEnum.DoNotConnect
  };
}
function _temp2(role) {
  return role.dashboardUx;
}
function _temp3() {
  return Sentry.setUser(null);
}
function _temp4() {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Session update() rejected during RefreshTokenError retry",
    level: "warning"
  });
}